<template>
  <BaseModal
    :value="show"
    @input="$emit('close')"
    class="after-confirmed-modal"
  >
    <template v-slot:header>
      <div class="text-xl font-bold">Order Confirmed Successfully! 🎉</div>
    </template>
    <div class="p-6 space-y-4">
      <div class="text-center mb-6">
        <p class="text-lg text-gray-700">
          Order <span class="font-bold">#{{ orderNumber }}</span> has been
          confirmed.
        </p>
        <p class="text-sm text-gray-500 mt-2">
          Complete these quick actions to finalize the order:
        </p>
      </div>

      <!-- Options Grid -->
      <div class="options-grid">
        <!-- 1. Send Order Email -->
        <div class="option-card" :class="{ completed: emailSent }">
          <div class="option-header">
            <div class="flex items-center gap-2">
              <span class="text-2xl">📧</span>
              <h3 class="font-semibold text-lg">Send Order Email</h3>
            </div>
            <div v-if="emailSent" class="check-mark">✓</div>
          </div>
          <p class="text-sm text-gray-600 mb-3">
            Send order confirmation to customer
          </p>
          <div class="mt-auto">
            <button
              @click="handleSendEmail"
              class="action-btn"
              :class="emailSent ? 'btn-completed' : 'btn-primary'"
              :disabled="sendingEmail || emailSent"
            >
              <span v-if="sendingEmail">Sending...</span>
              <span v-else-if="emailSent">Email Sent</span>
              <span v-else>Send Email</span>
            </button>
          </div>
        </div>

        <!-- 2. Manual Payment -->
        <div
          v-if="showPaymentOptions && !stripePaymentAdded"
          class="option-card"
          :class="{ completed: paymentAdded }"
        >
          <div class="option-header">
            <div class="flex items-center gap-2">
              <span class="text-2xl">💳</span>
              <h3 class="font-semibold text-lg">Manual Payment</h3>
            </div>
            <div v-if="paymentAdded" class="check-mark">✓</div>
          </div>
          <p class="text-sm text-gray-600 mb-3">
            Record a manual payment transaction
            <span v-if="!paymentAdded && pendingPayment > 0"
              >(Pending: ${{ pendingPayment }})</span
            >
          </p>

          <div class="mt-auto">
            <div v-if="!showManualPaymentForm && !paymentAdded">
              <button
                @click="showManualPaymentForm = true"
                class="action-btn btn-primary"
              >
                Add Payment
              </button>
            </div>

            <PaymentForm
              v-if="showManualPaymentForm && !paymentAdded"
              :amount="initialPendingPayment"
              :show-amount-field="true"
              :compact="true"
              :submitting="addingPayment"
              submit-text="Confirm Payment"
              submitting-text="Adding..."
              @submit="handleManualPayment"
              @cancel="cancelManualPayment"
            />

            <div v-if="paymentAdded" class="text-green-600 font-semibold">
              Payment Added Successfully
            </div>
          </div>
        </div>

        <!-- 3. Stripe Payment -->
        <div
          v-if="showPaymentOptions"
          class="option-card"
          :class="{ completed: stripePaymentAdded }"
        >
          <div class="option-header">
            <div class="flex items-center gap-2">
              <span class="text-2xl">💰</span>
              <h3 class="font-semibold text-lg">Stripe Payment</h3>
            </div>
            <div v-if="stripePaymentAdded" class="check-mark">✓</div>
          </div>
          <p class="text-sm text-gray-600 mb-3">
            Process payment through Stripe
            <span v-if="!stripePaymentAdded && pendingPayment > 0"
              >(Pending: ${{ pendingPayment }})</span
            >
          </p>

          <div class="mt-auto">
            <button
              v-if="!stripePaymentAdded"
              @click="handleStripePayment"
              class="action-btn btn-primary"
            >
              Process with Stripe
            </button>

            <div v-if="stripePaymentAdded" class="text-green-600 font-semibold">
              Payment Processed Successfully
            </div>
          </div>
        </div>

        <!-- 4. Continue as Credit -->
        <div
          v-if="isCreditOrder"
          class="option-card"
          :class="{ completed: creditConfirmed }"
        >
          <div class="option-header">
            <div class="flex items-center gap-2">
              <span class="text-2xl">🧾</span>
              <h3 class="font-semibold text-lg">Continue as Credit</h3>
            </div>
            <div v-if="creditConfirmed" class="check-mark">✓</div>
          </div>
          <p class="text-sm text-gray-600 mb-3">
            This order is on credit terms. Continue without recording a payment
            now.
          </p>

          <div class="mt-auto">
            <button
              v-if="!creditConfirmed"
              @click="handleContinueAsCredit"
              class="action-btn btn-primary"
            >
              Continue as Credit
            </button>

            <div v-else class="text-green-600 font-semibold">
              Order will proceed on credit
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="footer-actions">
        <button @click="handleSkip" class="btn-skip">
          Skip for Now
        </button>
        <button
          @click="handleComplete"
          class="btn-complete"
          :disabled="!canComplete"
        >
          Complete & Close
        </button>
      </div>
    </div>

    <!-- Stripe Payment Modal -->
    <StripePaymentModal
      v-if="showStripeModal"
      :show="showStripeModal"
      :amount="initialPendingPayment || pendingPayment"
      :order-number="orderNumber"
      :customer-email="customerEmail"
      @close="closeStripeModal"
      @payment-success="handleStripePaymentSuccess"
    />
  </BaseModal>
</template>

<script>
import { addPaymentToOrder } from "@/api/store/OrdersService.js";
import { sendOrderEmail } from "@/api/store/OrdersService.js";
import PaymentForm from "./PaymentForm.vue";
import StripePaymentModal from "./StripePaymentModal.vue";

export default {
  components: {
    PaymentForm,
    StripePaymentModal
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    orderNumber: {
      type: [String, Number],
      required: true
    },
    orderTotal: {
      type: [String, Number],
      required: true
    },
    customerEmail: {
      type: String,
      required: false,
      default: ""
    },
    pendingPayment: {
      type: [String, Number],
      required: false,
      default: 0
    },
    creditRetail: {
      type: String,
      required: false,
      default: ""
    }
  },
  data() {
    return {
      emailSent: false,
      sendingEmail: false,
      paymentAdded: false,
      stripePaymentAdded: false,
      addingPayment: false,
      showManualPaymentForm: false,
      showStripeModal: false,
      initialPendingPayment: 0,
      hadPendingPayment: false,
      creditConfirmed: false
    };
  },
  computed: {
    canComplete() {
      // User can complete if at least one action was taken
      // If payment was needed (initially), it must be added. Email is optional.
      if (this.hadPendingPayment) {
        return (
          this.emailSent ||
          this.paymentAdded ||
          this.stripePaymentAdded ||
          this.creditConfirmed
        );
      }
      // If no payment needed, just email is enough (or can skip)
      return true;
    },
    isCreditOrder() {
      return this.creditRetail === "CRED";
    },
    showPaymentOptions() {
      // Show payment options if there was initially a pending payment
      // Keep showing them even after payment is made (to show completion state)
      return this.hadPendingPayment;
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        // Reset all state when modal opens
        this.resetState();
        // Capture the initial pending payment
        this.initialPendingPayment = parseFloat(this.pendingPayment);
        this.hadPendingPayment = this.initialPendingPayment > 0;
      }
    }
  },
  methods: {
    resetState() {
      this.emailSent = false;
      this.sendingEmail = false;
      this.paymentAdded = false;
      this.stripePaymentAdded = false;
      this.addingPayment = false;
      this.showManualPaymentForm = false;
      this.showStripeModal = false;
      this.initialPendingPayment = 0;
      this.hadPendingPayment = false;
      this.creditConfirmed = false;
    },
    async handleSendEmail() {
      if (this.sendingEmail || this.emailSent) return;

      if (!this.customerEmail) {
        this.$toast.open({
          message: "No customer email available",
          type: "error",
          position: "top-right"
        });
        this.$emit("show-email-modal");
        return;
      }

      this.sendingEmail = true;
      try {
        await sendOrderEmail(this.orderNumber, {
          email: this.customerEmail,
          type: "order_confirmation"
        });

        this.emailSent = true;
        this.$toast.open({
          message: "Order email sent successfully",
          type: "success",
          position: "top-right"
        });
        this.$emit("email-sent");
      } catch (error) {
        console.error("Error sending email:", error);
        this.$toast.open({
          message: error.response?.data?.message || "Failed to send email",
          type: "error",
          position: "top-right"
        });
      } finally {
        this.sendingEmail = false;
      }
    },
    async handleManualPayment(paymentData) {
      this.addingPayment = true;
      try {
        await addPaymentToOrder(this.orderNumber, paymentData);

        this.paymentAdded = true;
        this.showManualPaymentForm = false;
        this.$toast.open({
          message: "Payment added successfully",
          type: "success",
          position: "top-right"
        });
        this.$emit("payment-added", paymentData);
      } catch (error) {
        console.error("Error adding payment:", error);
        this.$toast.open({
          message: error.response?.data?.message || "Failed to add payment",
          type: "error",
          position: "top-right"
        });
      } finally {
        this.addingPayment = false;
      }
    },
    cancelManualPayment() {
      this.showManualPaymentForm = false;
    },
    handleStripePayment() {
      this.showStripeModal = true;
    },
    handleStripePaymentSuccess(paymentData) {
      this.stripePaymentAdded = true;
      this.showStripeModal = false;
      this.$toast.open({
        message: "Stripe payment processed successfully",
        type: "success",
        position: "top-right"
      });
      this.$emit("payment-added", paymentData);
    },
    closeStripeModal() {
      this.showStripeModal = false;
    },
    handleContinueAsCredit() {
      this.creditConfirmed = true;
      this.$emit("continue-as-credit");
      this.$toast.open({
        message: "Order will proceed on credit",
        type: "success",
        position: "top-right"
      });
    },
    handleSkip() {
      if (confirm("Are you sure you want to skip these actions?")) {
        this.$emit("skip");
        this.$emit("close");
      }
    },
    handleComplete() {
      this.$emit("complete");
      this.$emit("close");
    }
  }
};
</script>

<style lang="postcss" scoped>
.after-confirmed-modal {
  .modal-content {
    max-width: 900px;
    max-height: 90vh;
    overflow-y: auto;
  }
}

.options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 300px));
  gap: 1.5rem;
  margin-bottom: 2rem;
  justify-content: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.option-card {
  border: 2px solid #d1d5db;
  border-radius: 0.5rem;
  padding: 1rem;
  background-color: white;
  transition: all 200ms;
  min-height: 180px;
  display: flex;
  flex-direction: column;
}

.option-card.completed {
  border-color: #10b981;
  background-color: #f0fdf4;
}

.option-card:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.option-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
}

.check-mark {
  width: 2rem;
  height: 2rem;
  background-color: #10b981;
  color: white;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 700;
}

.action-btn {
  width: 100%;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 600;
  transition: colors 200ms;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #2563eb;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #1d4ed8;
}

.btn-success {
  background-color: #16a34a;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background-color: #15803d;
}

.btn-secondary {
  background-color: #d1d5db;
  color: #374151;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #9ca3af;
}

.btn-completed {
  background-color: #10b981;
  color: white;
  cursor: default;
}

.footer-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid #d1d5db;
}

@media (max-width: 768px) {
  .footer-actions {
    flex-direction: column;
    gap: 0.5rem;
  }
}

.btn-skip {
  padding: 0.5rem 1.5rem;
  color: #4b5563;
  font-weight: 600;
  background: none;
  border: none;
  cursor: pointer;
}

.btn-skip:hover {
  color: #1f2937;
}

@media (max-width: 768px) {
  .btn-skip {
    width: 100%;
  }
}

.btn-complete {
  padding: 0.75rem 1.5rem;
  background-color: #16a34a;
  color: white;
  border-radius: 0.375rem;
  font-weight: 700;
  border: none;
  cursor: pointer;
}

.btn-complete:hover:not(:disabled) {
  background-color: #15803d;
}

.btn-complete:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .btn-complete {
    width: 100%;
  }
}
</style>
